// Link kinds step 2 — the dock's chat link card: a chat message's link playing in the stream dock. Figma: Shared / Dock
// / Chat link card 4358:3223 (Audio / Video), in Web street chat link card 4360:32088 (dark 4360:32296). Built like the
// wall playback card (WallDockCard: header, the player in WallDockPlayer → PlayableMedia, art + titles, play / pause,
// the mini player), for one link: no queue, no prev / next. Header: "From chat · <sender>", conversation · time,
// Go to message, full screen (link kinds: shared full-screen controls, like the wall card), collapse, stop.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, CircleStop, LocateFixed, Maximize, MessageSquareText, Minimize, Pause, Play, Square } from "lucide-react";
import type { WallItem } from "../../api/content";
import { isWallItemHeld } from "../../store/wallPlaybackStore";
import { useChatLinkPlaybackStore, type ChatLinkNowPlaying } from "../../store/chatLinkPlaybackStore";
import { STREET_CHAT_TYPES } from "../../store/notificationStore";
import { useAppOverlayStore } from "../../store/appOverlayStore";
import { useDockMiniStore } from "../../store/dockMiniStore";
import { CHAT_LINK_KEY } from "../../lib/dockMini";
import { kindIcon, kindOf } from "../../player";
import type { ChatLink } from "../../types/chat";
import WallDockPlayer from "./WallDockPlayer";
import MiniPlayerInfo, { type DockMini } from "./MiniPlayerInfo";
import { useElementFullscreen } from "./wallFullscreen";
import FullScreenPlayerControls from "../../player/controls/FullScreenPlayerControls";
import type { PlayerControlsSpec } from "../../player/controls/playerControlsSpec";
import { usePlayerControlsVisibility } from "../../player/controls/usePlayerControlsVisibility";
import { shownImage, useExternalMediaAllowed } from "../../lib/externalMedia";

/** The link as the dock player's item (one item, never in a post). */
export function chatLinkWallItem(link: ChatLink, key: string): WallItem {
  return {
    id: key, position: 0, type: link.type as WallItem["type"], url: link.url, externalId: link.externalId ?? null,
    fileId: null, thumbnailFileId: null, photoUrl: null, title: link.title ?? null, thumbnailUrl: link.thumbnailUrl ?? null,
    durationMs: null, width: null, height: null, provider: link.provider ?? null, codec: link.codec ?? null, live: link.live,
  };
}

/** Where Go to message opens the conversation: street chat for GC / temporary chats (and unknown), else pair chat. */
export function chatMessagePath(np: Pick<ChatLinkNowPlaying, "convId" | "convType">): string | null {
  if (np.convId == null) return null;
  return np.convType == null || STREET_CHAT_TYPES.has(np.convType)
    ? `/street-chat?conv=${encodeURIComponent(np.convId)}`
    : `/pair-chat/${encodeURIComponent(np.convId)}`;
}

const iconBtn = "shrink-0 flex items-center justify-center text-[var(--foreground)] disabled:opacity-40";

interface Props {
  /** Opens the message's conversation and jumps to it (StreamDock). */
  onGoToMessage?(np: ChatLinkNowPlaying): void;
  mini?: DockMini | null;
  hidden?: boolean;
}

export default function ChatLinkDockCard({ onGoToMessage, mini = null, hidden = false }: Props) {
  const externalOk = useExternalMediaAllowed();
  const { t } = useTranslation();
  const current = useChatLinkPlaybackStore((s) => s.current);
  const store = useChatLinkPlaybackStore.getState;
  const covered = useAppOverlayStore((s) => s.count > 0);
  const [clipped, setClipped] = useState(false);
  // YouTube shows its own controls (keyed by item, like WallDockCard): ours would duplicate them.
  const [ownControlsKey, setOwnControlsKey] = useState<string | null>(null);
  const itemKey = current ? `chat-link|${current.np.convId}|${current.np.localId ?? current.np.messageId}|${current.np.link.url}` : null;
  // Full screen is this card's wrapper around the player (WallDockCard): a link with a picture (embed) only.
  const [fsEl, setFsEl] = useState<HTMLDivElement | null>(null);
  const fs = useElementFullscreen(fsEl);
  const fsLink = current != null && kindOf(chatLinkWallItem(current.np.link, "")).surface === "embed";
  useEffect(() => {
    if (fs.on && !fsLink) fs.exit();
  }, [fs, fsLink]);
  const vis = usePlayerControlsVisibility({ active: fs.on, itemKey: itemKey ?? undefined });
  const fsPaused = fs.on && !!current?.paused;
  const { hold } = vis;
  useEffect(() => hold("paused", fsPaused), [hold, fsPaused]);
  const [tapToListen, setTapToListen] = useState<{ tap(): void } | null>(null);

  if (!current || !itemKey) return null;
  const { np, paused, failed } = current;
  const item = chatLinkWallItem(np.link, itemKey);
  const kind = kindOf(item);
  const link = kind.surface === "embed";
  const title = np.link.title || np.link.url;
  const provider = kind.meta(t);
  const time = np.timeStamp != null ? new Date(np.timeStamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null;
  const from = t("dock_chat_from", "From chat · {{sender}}", { sender: np.senderName ?? "" });
  const where = [np.convName, time].filter(Boolean).join(" · ");
  const held = isWallItemHeld(item, { hidden, covered, clipped });
  const miniVideo = mini != null && link;
  const canGo = onGoToMessage != null && chatMessagePath(np) != null;
  const Glyph = kindIcon(kind);
  const ownControls = kind.caps.ownControls && ownControlsKey === item.id;

  // Full screen (band under the 16:9 player, never over it: YouTube's policy): exit · title · stop, play / pause when
  // YouTube's own controls are not there.
  const fsSpec: PlayerControlsSpec = {
    embed: "bandUnder",
    title,
    meta: from,
    top: [
      { testId: "chat-link-dock-exit-fullscreen", label: t("exit_fullscreen"), icon: <Minimize aria-hidden="true" />, onClick: fs.exit, start: true },
      { testId: "chat-link-dock-fs-stop", label: t("dock_stop", "Stop"), icon: <Square aria-hidden="true" />, onClick: () => store().stop(), variant: "destructive" },
    ],
    middle: ownControls
      ? []
      : [{
          testId: "chat-link-dock-fs-playpause",
          label: paused ? t("dock_play", "Play") : t("dock_pause", "Pause"),
          icon: paused ? <Play aria-hidden="true" /> : <Pause aria-hidden="true" />,
          onClick: () => store().togglePause(),
        }],
    bottomExtra: fs.on && tapToListen ? (
      <button
        type="button"
        data-testid="btn-tap-to-listen"
        onClick={tapToListen.tap}
        className="h-8 rounded-full bg-white/16 px-4 text-xs font-medium text-white focus-visible:outline-2 focus-visible:outline-white"
      >
        ▶ {t("tap_to_listen", "Tap to listen")}
      </button>
    ) : undefined,
  };

  const goBtn = (cls: string) =>
    canGo && (
      <button
        type="button"
        data-testid="dock-go-to-message"
        className={cls}
        aria-label={t("dock_go_to_message", "Go to message")}
        title={t("dock_go_to_message", "Go to message")}
        onClick={() => onGoToMessage?.(np)}
      >
        <LocateFixed size={18} aria-hidden="true" />
      </button>
    );

  const thumb = shownImage(np.link.thumbnailUrl, externalOk);
  const art = thumb ? (
    <img src={thumb} alt="" className={`${mini ? "size-12 rounded-lg" : "size-16 rounded-xl"} shrink-0 object-cover`} />
  ) : (
    <div className={`${mini ? "size-12 rounded-lg" : "size-16 rounded-xl"} shrink-0 bg-[var(--bg-elevated)] text-[var(--foreground-muted)] flex items-center justify-center`}>
      <Glyph size={24} aria-hidden="true" />
    </div>
  );

  return (
    <div
      data-testid="chat-link-dock-card"
      data-mini={mini ? (miniVideo ? "video" : "audio") : undefined}
      data-held={held ? "true" : undefined}
      className={`relative flex shrink-0 overflow-hidden rounded-xl bg-[var(--bg-base)] shadow-lg ${
        mini ? `flex-row gap-3 p-2 ${miniVideo ? "items-stretch" : "items-center"}` : "flex-col pb-2"
      }`}
      style={hidden ? { display: "none" } : undefined}
    >
      {/* The player stays the second child in every form: floating ↔ mini never remounts it. */}
      {mini ? (
        !miniVideo && art
      ) : (
        <div className="flex items-center gap-2 p-2">
          <div className="size-8 shrink-0 rounded-full bg-[var(--bg-elevated)] text-[var(--foreground)] flex items-center justify-center">
            <MessageSquareText size={18} aria-hidden="true" />
          </div>
          <div className="flex flex-1 min-w-0 flex-col">
            <span data-testid="chat-link-dock-from" className="truncate text-sm font-bold leading-none text-[var(--foreground)]">{from}</span>
            <span className="truncate text-xs leading-[1.3] text-[var(--foreground-muted)]">{where}</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {goBtn(iconBtn)}
            {fsLink && fs.supported && (
              <button
                type="button"
                data-testid="chat-link-dock-fullscreen"
                className={iconBtn}
                aria-label={t("fullscreen")}
                title={t("fullscreen")}
                onClick={fs.toggle}
              >
                <Maximize size={20} aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              data-testid="chat-link-dock-collapse"
              className={iconBtn}
              aria-label={t("dock_collapse", "Collapse")}
              title={t("dock_collapse", "Collapse")}
              onClick={() => useDockMiniStore.getState().minimise(CHAT_LINK_KEY)}
            >
              <ChevronDown size={20} aria-hidden="true" />
            </button>
            <button
              type="button"
              data-testid="chat-link-dock-stop"
              className={iconBtn}
              aria-label={t("dock_stop", "Stop")}
              title={t("dock_stop", "Stop")}
              onClick={() => store().stop()}
            >
              <CircleStop size={18} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}

      <div
        ref={setFsEl}
        data-testid="chat-link-dock-fs-wrap"
        data-fullscreen={fs.on ? "true" : undefined}
        className={fs.on ? "bg-black" : link && !mini ? "shrink-0 px-2" : "shrink-0"}
      >
        <FullScreenPlayerControls spec={fsSpec} visibility={vis} on={fs.on}>
        <WallDockPlayer
          item={item}
          paused={paused || held}
          held={held}
          mini={mini != null}
          fullscreen={fs.on}
          onTapToListen={(tap) => setTapToListen(tap ? { tap } : null)}
          onClipped={setClipped}
          onOwnControls={(on) => setOwnControlsKey(on ? item.id : null)}
          onUserPaused={(p) => {
            if (store().current?.paused !== p) store().togglePause();
          }}
          onEnded={() => store().ended()}
          onError={() => store().failed()}
          onProgress={() => {}}
        />
        </FullScreenPlayerControls>
      </div>

      {mini ? (
        <MiniPlayerInfo
          mini={mini}
          title={title}
          line={from}
          video={miniVideo}
          playing={!(paused || held)}
          onTogglePlay={() => store().togglePause()}
          onStop={() => store().stop()}
          actions={goBtn("size-10 shrink-0 rounded-full flex items-center justify-center text-[var(--foreground)] hover:bg-[var(--bg-hover)]")}
        />
      ) : (
        <div className="flex flex-col gap-2 px-2 pt-2">
          <div className="flex items-center gap-3">
            {art}
            <div className="flex flex-1 min-w-0 flex-col gap-0.5">
              <span className="truncate text-sm font-bold leading-none text-[var(--foreground)]">{title}</span>
              <span className="truncate text-xs leading-[1.3] text-[var(--foreground-muted)]">{provider}</span>
              {failed && (
                <span role="status" data-testid="chat-link-dock-failed" className="truncate text-xs leading-[1.3] text-[var(--destructive)]">
                  {t("cant_play_link", "Can't play this link")}
                </span>
              )}
            </div>
          </div>
          {/* YouTube's own controls play and pause it (Figma Media=Video); the rest get ours. */}
          {!ownControls && (
            <div className="flex items-center justify-center">
              <button
                type="button"
                data-testid="chat-link-dock-playpause"
                className="size-10 shrink-0 rounded-full bg-[var(--primary)] text-[var(--primary-foreground)] flex items-center justify-center"
                aria-label={paused ? t("dock_play", "Play") : t("dock_pause", "Pause")}
                title={paused ? t("dock_play", "Play") : t("dock_pause", "Pause")}
                onClick={() => store().togglePause()}
              >
                {paused ? <Play size={24} aria-hidden="true" /> : <Pause size={24} aria-hidden="true" />}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
